<template>
  <div class="create-edit-address-card">
    <div name="address-type-topic-container" class="row card__header">
      <span class="col-7 d-flex align-items-center card__header--text">{{
        addressType
      }}</span>
      <div
        v-if="isShowCopyButton && hasEditPermission"
        class="col-5 d-flex justify-content-end copy-button-container m-0"
      >
        <atom-button
          :id="`copy-from-main-address-${addressType}`"
          buttonId="copy-from-main-address"
          buttonStyle="border"
          iconLeft="CopyIcon"
          :isDisable="!hasEditPermission"
          @click="onClickCopyFromMainAddress"
        >
          <span class="text-copy">Address</span>
        </atom-button>

        <b-tooltip
          :target="`copy-from-main-address-${addressType}`"
          placement="top"
          boundary="window"
        >
          <div class="description__container">
            <div>Copy from Main Address</div>
          </div>
        </b-tooltip>
      </div>
    </div>
    <div name="organization-name-container" class="row mt-2">
      <div class="col-12">
        <atom-input
          v-model="addressDetail.organizationName"
          title="Organization name"
          placeholder="Organization name"
          :isAsterisk="true"
          :isError="isErrorAtOrganizationName"
          :errorMessage="errorMessageAtInput"
          :isDisable="!hasEditPermission"
        ></atom-input>
      </div>
    </div>
    <div name="business-type-and-vat-container" class="row mt-1">
      <div :class="[isShowVat ? 'col-8' : 'col-12']">
        <atom-select
          v-model="reactiveBusinessType"
          title="Business Type"
          placeholder="Business type"
          label="businessTypeName"
          :isAsterisk="true"
          :isClearable="false"
          :isAppendToBody="false"
          :isEnableProper="false"
          :isError="isErrorAtBusinessType"
          :errorMessage="errorMessageAtSelector"
          :option="businessTypeOption"
          :isDisable="!hasEditPermission"
        ></atom-select>
      </div>
      <div v-if="isShowVat" class="col-4">
        <atom-input
          v-model="addressDetail.vat"
          title="Vat (%)"
          placeholder="Vat"
          :isAsterisk="true"
          :isError="isErrorAtVat"
          :isDisable="!hasEditPermission"
        ></atom-input>
      </div>
    </div>
    <div name="address-topic-container" class="row mt-2">
      <span class="col-12 text--topic-medium">ADDRESS</span>
    </div>
    <div name="country-container" class="row mt-1">
      <div class="col-12">
        <atom-select
          v-model="addressDetail.country"
          title="Country"
          placeholder="Country"
          :isAsterisk="true"
          :isClearable="false"
          :isAppendToBody="false"
          :isEnableProper="false"
          :isError="isErrorAtCountry"
          :errorMessage="errorMessageAtSelector"
          :option="countryOption"
          :isDisable="!hasEditPermission"
        ></atom-select>
      </div>
    </div>
    <div name="province-container" class="row mt-1">
      <div class="col-12">
        <atom-select
          v-if="isSelectThailand"
          v-model="addressDetail.province"
          title="Province"
          placeholder="Province"
          label="name_th"
          :isDisable="!addressDetail.country || !hasEditPermission"
          :isAsterisk="true"
          :isClearable="false"
          :isAppendToBody="false"
          :isEnableProper="false"
          :isError="isErrorAtProvince"
          :errorMessage="errorMessageAtSelector"
          :option="provinceOption"
        ></atom-select>
        <atom-input
          v-else
          v-model="addressDetail.province"
          title="Province"
          placeholder="Province"
          :isAsterisk="true"
          :isError="isErrorAtDistrict"
          :errorMessage="errorMessageAtInput"
          :isDisable="!hasEditPermission"
        ></atom-input>
      </div>
    </div>
    <div name="district-container" class="row mt-1">
      <div class="col-12">
        <atom-select
          v-if="isSelectThailand"
          v-model="addressDetail.district"
          title="District"
          placeholder="District"
          label="name_th"
          :isDisable="!addressDetail.province || !hasEditPermission"
          :isAsterisk="true"
          :isClearable="false"
          :isAppendToBody="false"
          :isEnableProper="false"
          :isError="isErrorAtDistrict"
          :errorMessage="errorMessageAtSelector"
          :option="districtOption"
        ></atom-select>
        <atom-input
          v-else
          v-model="addressDetail.district"
          title="District"
          placeholder="District"
          :isAsterisk="true"
          :isError="isErrorAtDistrict"
          :errorMessage="errorMessageAtInput"
          :isDisable="!hasEditPermission"
        ></atom-input>
      </div>
    </div>
    <div name="sub-district-container" class="row mt-1">
      <div class="col-12">
        <atom-select
          v-if="isSelectThailand"
          v-model="addressDetail.subDistrict"
          title="Sub-District"
          placeholder="Sub-District"
          label="name_th"
          :isDisable="!addressDetail.district || !hasEditPermission"
          :isAsterisk="true"
          :isClearable="false"
          :isAppendToBody="false"
          :isEnableProper="false"
          :isError="isErrorAtSubDistrict"
          :errorMessage="errorMessageAtSelector"
          :option="subDistrictOption"
        ></atom-select>
        <atom-input
          v-else
          v-model="addressDetail.subDistrict"
          title="Sub-District"
          placeholder="Sub-District"
          :isAsterisk="true"
          :isError="isErrorAtSubDistrict"
          :errorMessage="errorMessageAtInput"
          :isDisable="!hasEditPermission"
        ></atom-input>
      </div>
    </div>
    <div name="address-detail-container" class="row mt-1">
      <div class="col-12">
        <atom-input
          v-model="addressDetail.address"
          title="Address"
          placeholder="Address"
          :isAsterisk="true"
          :isError="isErrorAtAddress"
          :errorMessage="errorMessageAtInput"
          :isDisable="!hasEditPermission"
        ></atom-input>
      </div>
    </div>
    <div name="postal-code-container" class="row mt-1">
      <div class="col-12">
        <atom-select
          v-if="isSelectThailand"
          v-model="addressDetail.postalCode"
          title="Postal code"
          placeholder="Postal code"
          label="zip_code"
          :isDisable="!addressDetail.subDistrict || !hasEditPermission"
          :isAsterisk="true"
          :isClearable="false"
          :isAppendToBody="false"
          :isEnableProper="false"
          :isError="isErrorAtPostalCode"
          :errorMessage="errorMessageAtSelector"
          :option="zipCodeOption"
        ></atom-select>
        <atom-input
          v-else
          v-model="addressDetail.postalCode"
          title="Postal code"
          placeholder="Postal code"
          :isAsterisk="true"
          :isError="isErrorAtPostalCode"
          :errorMessage="errorMessageAtInput"
          :isDisable="!hasEditPermission"
        ></atom-input>
      </div>
    </div>
    <div name="contact-topic-container" class="row mt-2">
      <span class="col-12 text--topic-medium">CONTACT</span>
    </div>
    <div name="email-container" class="row mt-1">
      <div class="col-12">
        <atom-input
          v-model="addressDetail.email"
          title="Email"
          placeholder="Email"
          :isDisable="!hasEditPermission"
        ></atom-input>
      </div>
    </div>
    <div name="phone-container" class="row mt-1">
      <div class="col-12">
        <atom-input
          v-model="addressDetail.phone"
          title="Phone"
          placeholder="Phone"
          :isDisable="!hasEditPermission"
        ></atom-input>
      </div>
    </div>
    <div name="fax-container" class="row mt-1">
      <div class="col-12">
        <atom-input
          v-model="addressDetail.fax"
          title="Fax"
          placeholder="Fax"
          :isDisable="!hasEditPermission"
        ></atom-input>
      </div>
    </div>
  </div>
</template>

<script>
import { CountryList } from "@/constants/countryList"
import { ProvinceList } from "@/constants/provinceList"
import { DistrictList } from "@/constants/districtList"
import { SubDistrictList } from "@/constants/subDistrictList"
import { ZipCodeList } from "@/constants/zipCodeList"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import mixinsValidator from "@/mixins/mixinsValidator"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import { BTooltip } from "bootstrap-vue"

export default {
  name: "MoleculeCreateEditAddressCard.vue",
  components: { AtomButton, AtomInput, AtomSelect, BTooltip },
  mixins: [mixinsValidator, mixinsObjectHelper],
  props: {
    addressType: {
      type: String,
      required: true,
    },
    mainAddressDetail: {
      type: Object,
      required: false,
      default: null,
    },
    businessTypeOption: {
      type: Array,
      required: false,
      default: null,
    },
    isValidateAddressTrigger: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasEditPermission: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: {
      type: Object,
      required: true,
    },
  },
  emits: ["onUpdateErrorInCreateEditAddress"],
  data() {
    return {
      reactiveBusinessType: null,
      errorMessageAtInput: "Please enter valid data",
      errorMessageAtSelector: "Please select data",
      isFirstTimeMappingAddressData: true,
    }
  },
  computed: {
    addressDetail: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit("input", value)
      },
    },
    customerId() {
      return this.$route?.params?.id
    },
    countryOption() {
      return CountryList
    },
    provinceOption() {
      return ProvinceList
    },
    districtOption() {
      return this.addressDetail.province
        ? DistrictList.filter(
            (district) =>
              district.province_id === this.addressDetail.province.id
          )
        : []
    },
    subDistrictOption() {
      return this.addressDetail.district
        ? SubDistrictList.filter(
            (subDistrict) =>
              subDistrict.amphure_id === this.addressDetail.district.id
          )
        : []
    },
    zipCode() {
      return this.addressDetail.subDistrict
        ? ZipCodeList.filter(
            (zipCode) => zipCode.amphure_id === this.addressDetail.district.id
          )
        : []
    },
    zipCodeOption() {
      return this.addressDetail.subDistrict
        ? [...new Set(this.zipCode.map((zipCode) => zipCode.zip_code))]
        : []
    },
    isShowCopyButton() {
      return (
        this.addressType?.toLowerCase() === "billing address" ||
        this.addressType?.toLowerCase() === "mailing address"
      )
    },
    isShowVat() {
      return this.addressType?.toLowerCase() === "billing address"
    },
    isSelectThailand() {
      return this.addressDetail?.country?.toLowerCase() === "thailand"
    },
    isErrorAtOrganizationName() {
      return this.isValidateAddressTrigger
        ? this.isEmptyString(this.addressDetail.organizationName)
        : false
    },
    isErrorAtBusinessType() {
      return this.isValidateAddressTrigger
        ? this.isEmptyObject(this.addressDetail.businessType)
        : false
    },
    isErrorAtVat() {
      if (this.isShowVat && this.isValidateAddressTrigger) {
        return this.isNotNumeric(this.addressDetail.vat)
      } else {
        return false
      }
    },
    isErrorAtCountry() {
      return this.isValidateAddressTrigger
        ? this.isEmptyObject(this.addressDetail.country)
        : false
    },
    isErrorAtProvince() {
      if (this.isValidateAddressTrigger)
        return this.isSelectThailand
          ? this.isEmptyObject(this.addressDetail.province)
          : this.isEmptyString(this.addressDetail.province)
      else return false
    },
    isErrorAtDistrict() {
      if (this.isValidateAddressTrigger)
        return this.isSelectThailand
          ? this.isEmptyObject(this.addressDetail.district)
          : this.isEmptyString(this.addressDetail.district)
      else return false
    },
    isErrorAtSubDistrict() {
      if (this.isValidateAddressTrigger)
        return this.isSelectThailand
          ? this.isEmptyObject(this.addressDetail.subDistrict)
          : this.isEmptyString(this.addressDetail.subDistrict)
      else return false
    },
    isErrorAtAddress() {
      return this.isValidateAddressTrigger
        ? this.isEmptyString(this.addressDetail.address)
        : false
    },
    isErrorAtPostalCode() {
      return this.isValidateAddressTrigger
        ? this.isNotNumeric(this.addressDetail.postalCode)
        : false
    },
    isErrorOccur() {
      return (
        this.isErrorAtOrganizationName ||
        this.isErrorAtBusinessType ||
        this.isErrorAtVat ||
        this.isErrorAtCountry ||
        this.isErrorAtProvince ||
        this.isErrorAtDistrict ||
        this.isErrorAtSubDistrict ||
        this.isErrorAtAddress ||
        this.isErrorAtPostalCode
      )
    },
  },
  watch: {
    addressDetail: {
      deep: true,
      handler() {
        if (
          this.customerId &&
          this.addressDetail &&
          this.isFirstTimeMappingAddressData
        ) {
          this.reactiveBusinessType = this.addressDetail.businessType
          this.mapAddressData()
          this.mapBusinessTypeData()
        }
        if (this.isValidateAddressTrigger) {
          this.$emit("onUpdateErrorInCreateEditAddress", this.isErrorOccur)
        }
      },
    },
    reactiveBusinessType: {
      deep: true,
      handler() {
        this.addressDetail.businessType = this.deepCopyObject(
          this.reactiveBusinessType
        )
      },
    },
    "addressDetail.country": {
      deep: true,
      handler(newValue, oldValue) {
        const notFirstTimeMapData =
          !this.isFirstTimeMappingAddressData && this.customerId

        const baseCondition =
          !!oldValue &&
          oldValue !== newValue &&
          newValue !== this.mainAddressDetail?.country

        if (baseCondition && notFirstTimeMapData) {
          this.setDefaultAddress("country")
        } else if (baseCondition) {
          this.setDefaultAddress("country")
        }
      },
    },
    "addressDetail.province": {
      deep: true,
      handler(newValue, oldValue) {
        const notFirstTimeMapData =
          !this.isFirstTimeMappingAddressData && this.customerId

        const baseCondition =
          !!oldValue &&
          newValue !== this.mainAddressDetail?.province &&
          this.addressDetail?.country?.toLowerCase() === "thailand"

        if (baseCondition && notFirstTimeMapData) {
          this.setDefaultAddress("province")
        } else if (baseCondition) {
          this.setDefaultAddress("province")
        }
      },
    },
    "addressDetail.district": {
      deep: true,
      handler(newValue, oldValue) {
        const notFirstTimeMapData =
          !this.isFirstTimeMappingAddressData && this.customerId

        const baseCondition =
          !!oldValue &&
          newValue !== this.mainAddressDetail?.district &&
          this.addressDetail?.country?.toLowerCase() === "thailand"

        if (baseCondition && notFirstTimeMapData) {
          this.setDefaultAddress("district")
        } else if (baseCondition) {
          this.setDefaultAddress("district")
        }
      },
    },
    "addressDetail.subDistrict": {
      deep: true,
      handler(newValue, oldValue) {
        const baseCondition =
          !!oldValue &&
          newValue !== this.mainAddressDetail?.subDistrict &&
          this.addressDetail?.country?.toLowerCase() === "thailand"

        const notFirstTimeMapData =
          !this.isFirstTimeMappingAddressData && this.customerId

        if (baseCondition && notFirstTimeMapData) {
          this.setDefaultAddress("subDistrict")
        } else if (baseCondition) {
          this.setDefaultAddress("subDistrict")
        }
      },
    },
    isValidateAddressTrigger: {
      immediate: true,
      handler() {
        this.$emit("onUpdateErrorInCreateEditAddress", this.isErrorOccur)
      },
    },
  },

  mounted() {
    this.isFirstTimeMappingAddressData = true
  },

  methods: {
    mapAddressData() {
      // start to map province string to provinceObj to get id
      const provinceData = ProvinceList.find(
        (province) => province.name_th === this.addressDetail?.province
      )
      this.addressDetail.province = provinceData
        ? this.deepCopyObject(provinceData)
        : this.addressDetail.province

      // start to map district string to districtObj to get id
      const districtData = DistrictList.find(
        (district) =>
          district.province_id == this.addressDetail.province.id &&
          district.name_th === this.addressDetail.district
      )
      this.addressDetail.district = districtData
        ? this.deepCopyObject(districtData)
        : this.addressDetail.district

      // start to map subDistrict string to subDistrictObj to get id
      const subDistrictData = SubDistrictList.find(
        (subDistrict) =>
          subDistrict.amphure_id == this.addressDetail.district.id &&
          subDistrict.name_th === this.addressDetail.subDistrict
      )
      this.addressDetail.subDistrict = subDistrictData
        ? this.deepCopyObject(subDistrictData)
        : this.addressDetail.subDistrict

      // start to map postalCode string to postalCodeObj to get id
      const postalCodeData = ZipCodeList.find(
        (zipCode) =>
          zipCode.zip_code == this.addressDetail.postalCode &&
          zipCode.amphure_id == this.addressDetail.district.id
      )
      this.addressDetail.postalCode = postalCodeData
        ? this.deepCopyObject(postalCodeData)
        : this.addressDetail.postalCode

      // set flag that show map data from api complete
      this.isFirstTimeMappingAddressData = false
    },

    mapBusinessTypeData() {
      const businessTypeData = this.businessTypeOption.find(
        (type) => type.businessTypeId === this.addressDetail.businessTypeId
      )
      this.addressDetail.businessType = businessTypeData
        ? this.deepCopyObject(businessTypeData)
        : this.addressDetail.businessType
    },

    setDefaultAddress(changeAddressField) {
      switch (changeAddressField) {
        case "country":
          this.addressDetail.province = null
        case "province":
          this.addressDetail.district = null
        case "district":
          this.addressDetail.subDistrict = null
        case "subDistrict":
          this.addressDetail.postalCode = null
      }
    },

    onClickCopyFromMainAddress() {
      if (this.addressType?.toLowerCase() === "billing address") {
        const vatValue = this.addressDetail.vat
        Object.assign(this.addressDetail, this.mainAddressDetail)
        this.addressDetail.vat = vatValue
        this.reactiveBusinessType = this.deepCopyObject(
          this.mainAddressDetail.businessType
        )
      } else {
        Object.assign(this.addressDetail, this.mainAddressDetail)
        this.reactiveBusinessType = this.deepCopyObject(
          this.mainAddressDetail.businessType
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.create-edit-address-card ::v-deep input.vs__search::placeholder {
  color: #b9b9c3;
}

.text-copy {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.create-edit-address-card {
  width: 100%;
  border: 1px solid #b9b9c3;
  border-radius: 6px;
  padding: 18px;
}

.card__header--text {
  font-weight: 600;
  font-size: 12px;
  color: #6e6b7b;
  height: 38px;
}

.copy-button-container {
  margin-top: 6px;
}

.text--topic-medium {
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
  color: #b9b9c3;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}
</style>
